import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useDebounce } from 'usehooks-ts';
import { useNavigate } from 'react-router-dom';
import useBoundStore from 'store';
import useApartmentsSecondaryFilterLimits from '../../api/hooks/use-apartments-secondary-filter-limits';
import useLimitsSecondary from '../../api/hooks/use-limits-secondary';
import { MarketTypes } from '../../api/types';
import { RangeNumber } from 'lib/types';
import { BuyApartmentFlowPaths } from '../../routes';
import {
  findMatchingIdsForMultipleIds,
  formatArea,
  formatViewObjectsPhrase,
  toReadableNumber,
  toReadablePrice,
  transformTypeFacility,
} from 'lib/utils';
import Collapse from 'components/collapse';
import TextInput from 'components/text-input';
import BaseScreen from 'components/base-screen';
import { ButtonProps } from 'components/button';
import InputsWithRangeSlider, {
  getPlaceholdersForInputsWithRangeSlider,
} from 'components/inputs-with-range-slider';
import magnifier from 'assets/images/magnifier.svg';
import styles from './styles.module.scss';
import CheckboxList from '../../components/checkbox-list';
import PolygonFilterData from 'components/items-ymap/Polygon/PolygonFilterData';
import { IPolygonItemRequest } from 'api/hooks/use-apartments-limits-secondary';
import { SQ_METER_SIGN } from 'lib/constants';
import Loader from 'components/loader';

const BuySecondaryFilters: React.FC = () => {
  const navigate = useNavigate();
  const { filterLimits, isLoading: isLoadingFilters } = useApartmentsSecondaryFilterLimits();
  const storePriceRangeMin = useBoundStore((s) => s.priceRangeMin);
  const storePriceRangeMax = useBoundStore((s) => s.priceRangeMax);
  const storePriceRangePerSqmMin = useBoundStore((s) => s.priceRangePerSqmMin);
  const storePriceRangePerSqmMax = useBoundStore((s) => s.priceRangePerSqmMax);
  const storeSquareFootageRangeMin = useBoundStore((s) => s.areaSqFootageRangeMin);
  const storeSquareFootageRangeMax = useBoundStore((s) => s.areaSqFootageRangeMax);

  const secondaryDifferenceWithMarketPriceMin = useBoundStore(
    (s) => s.secondaryDifferenceWithMarketPriceMin,
  );
  const secondaryDifferenceWithMarketPriceMax = useBoundStore(
    (s) => s.secondaryDifferenceWithMarketPriceMax,
  );

  const setFiltersSecondary = useBoundStore((s) => s.setFiltersSecondary);

  // room-types
  const selectedApartmentRoomTypes = useBoundStore((s) => s.selectedApartmentRoomTypes);
  const toggleSelectedApartmentRoomTypes = useBoundStore((s) => s.toggleSelectedApartmentRoomTypes);
  const clearApartmentRoomTypes = useBoundStore((s) => s.clearApartmentRoomTypes);

  const [search, setSearch] = useState('');
  const { count, isLoading } = useLimitsSecondary(true, MarketTypes.SECONDARY);

  const [differenceWithMarketPrice, setDifferenceWithMarketPrice] = useState<[number?, number?]>([
    secondaryDifferenceWithMarketPriceMin,
    secondaryDifferenceWithMarketPriceMax,
  ]);

  const [priceRangePerSqm, setPriceRangePerSqm] = useState<[number?, number?]>([
    storePriceRangePerSqmMin,
    storePriceRangePerSqmMax,
  ]);

  const [priceRange, setPriceRange] = useState<[number?, number?]>([
    storePriceRangeMin,
    storePriceRangeMax,
  ]);

  const [squareFootageRange, setSquareFootageRange] = useState<[number?, number?]>([
    storeSquareFootageRangeMin,
    storeSquareFootageRangeMax,
  ]);

  const buttons: ButtonProps[] = [
    {
      loading: isLoading,
      label: isLoading ? 'Загрузка' : formatViewObjectsPhrase(count),
      disabled: isLoading || count === undefined,
      variant: 'default',
      onClick: () => navigate(BuyApartmentFlowPaths.apartmentListBuySecondary),
    },
  ];

  const filterParams = useMemo(
    () => ({ priceRange, squareFootageRange, priceRangePerSqm, differenceWithMarketPrice }),
    [priceRange, squareFootageRange, priceRangePerSqm, differenceWithMarketPrice],
  );

  const debouncedFilterParams = useDebounce(filterParams);

  useEffect(() => {
    const priceRange: RangeNumber = {};
    if (debouncedFilterParams.priceRange[0]) {
      priceRange.min = debouncedFilterParams.priceRange[0];
    }
    if (debouncedFilterParams.priceRange[1]) {
      priceRange.max = debouncedFilterParams.priceRange[1];
    }
    const areaSqFootageRange: RangeNumber = {};

    if (debouncedFilterParams.squareFootageRange[0]) {
      areaSqFootageRange.min = debouncedFilterParams.squareFootageRange[0];
    }
    if (debouncedFilterParams.squareFootageRange[1]) {
      areaSqFootageRange.max = debouncedFilterParams.squareFootageRange[1];
    }

    const priceRangePerSqm: RangeNumber = {};

    if (debouncedFilterParams.priceRangePerSqm[0]) {
      priceRangePerSqm.min = debouncedFilterParams.priceRangePerSqm[0];
    }
    if (debouncedFilterParams.priceRangePerSqm[1]) {
      priceRangePerSqm.max = debouncedFilterParams.priceRangePerSqm[1];
    }

    const secondaryDifferenceWithMarketPrice: RangeNumber = {};

    if (debouncedFilterParams.differenceWithMarketPrice[0]) {
      secondaryDifferenceWithMarketPrice.min = debouncedFilterParams.differenceWithMarketPrice[0];
    }
    if (debouncedFilterParams.differenceWithMarketPrice[1]) {
      secondaryDifferenceWithMarketPrice.max = debouncedFilterParams.differenceWithMarketPrice[1];
    }

    setFiltersSecondary({
      priceRangeMax: priceRange.max,
      priceRangeMin: priceRange.min,
      areaSqFootageRangeMin: areaSqFootageRange.min,
      areaSqFootageRangeMax: areaSqFootageRange.max,
      priceRangePerSqmMin: priceRangePerSqm.min,
      priceRangePerSqmMax: priceRangePerSqm.max,
      secondaryDifferenceWithMarketPriceMin: secondaryDifferenceWithMarketPrice.min,
      secondaryDifferenceWithMarketPriceMax: secondaryDifferenceWithMarketPrice.max,
    });
  }, [setFiltersSecondary, debouncedFilterParams]);

  const searchMatch = (str: string) => {
    return search !== '' && !str.toLowerCase().includes(search.toLowerCase());
  };

  const getCollapseProps = (header: string) => ({
    header,
    className: clsx(styles.filterItem, searchMatch(header) && styles.hidden),
  });

  const transformedTypes = !!filterLimits?.facilityTypes?.length
    ? transformTypeFacility(filterLimits?.facilityTypes)?.map((i) => ({
        id: i.id,
        label: i.fullTitle,
      }))
    : [];

  const handleSelectTypeRoom = (facilityTypeIds: number) => {
    const facilityTypeDataIds = findMatchingIdsForMultipleIds(
      filterLimits?.facilityTypes ?? [],
      [facilityTypeIds] ?? [],
    );
    toggleSelectedApartmentRoomTypes(facilityTypeDataIds[0]);
  };

  const selectedPolygonSecondary = useBoundStore((s) => s.selectedPolygonSecondary);
  const setSelectedPolygonSecondary = useBoundStore((s) => s.setSelectedPolygonSecondary);

  const handleClearPolygon = () => {
    setSelectedPolygonSecondary(null);
  };

  return (
    <BaseScreen
      padContent
      title='Фильтры вторички'
      buttons={buttons}
      footerClassName={styles.footer}
      floatingButtons={false}
    >
      {isLoadingFilters ? (
        <div className={styles.loader}>
          <Loader />
        </div>
      ) : (
        <div className={styles.container}>
          <TextInput
            tall
            value={search}
            icon={magnifier}
            onChange={setSearch}
            className={styles.search}
            placeholder='Поиск по фильтрам'
          />

          {filterLimits && (
            <Collapse {...getCollapseProps('Количество комнат')}>
              <CheckboxList
                items={transformedTypes}
                selected={selectedApartmentRoomTypes}
                onChange={handleSelectTypeRoom}
                onClear={clearApartmentRoomTypes}
              />
            </Collapse>
          )}

          <Collapse {...getCollapseProps('Цена')}>
            <InputsWithRangeSlider
              withSlider={false}
              value={priceRange}
              onChange={setPriceRange}
              inputVariant='price'
              min={filterLimits?.priceMin ?? 0}
              max={filterLimits?.priceMax ?? 100000000}
              step={1_000}
              placeholders={getPlaceholdersForInputsWithRangeSlider(
                filterLimits?.priceMin,
                filterLimits?.priceMax,
                toReadablePrice,
              )}
            />
          </Collapse>

          <Collapse {...getCollapseProps(`Цена за 1 ${SQ_METER_SIGN}`)}>
            <InputsWithRangeSlider
              withSlider={false}
              value={priceRangePerSqm}
              onChange={setPriceRangePerSqm}
              inputVariant='priceSqM'
              min={filterLimits?.pricePerSqMMin ?? 0}
              max={filterLimits?.pricePerSqMMax ?? 100000000}
              step={1_000}
              placeholders={getPlaceholdersForInputsWithRangeSlider(
                filterLimits?.pricePerSqMMin,
                filterLimits?.pricePerSqMMax,
                toReadablePrice,
              )}
            />
          </Collapse>

          <Collapse {...getCollapseProps(`Разница с рыночной стоимостью %`)}>
            <InputsWithRangeSlider
              withSlider={false}
              value={differenceWithMarketPrice}
              onChange={setDifferenceWithMarketPrice}
              inputVariant='percent'
              min={filterLimits?.secondaryMarketPriceDifferenceMin ?? -1000}
              max={filterLimits?.secondaryMarketPriceDifferenceMax ?? 1000}
              step={1_000}
              placeholders={getPlaceholdersForInputsWithRangeSlider(
                filterLimits?.secondaryMarketPriceDifferenceMin,
                filterLimits?.secondaryMarketPriceDifferenceMax,
                toReadableNumber,
              )}
            />
          </Collapse>

          <Collapse {...getCollapseProps('Площадь')}>
            <InputsWithRangeSlider
              withSlider={false}
              value={squareFootageRange}
              onChange={setSquareFootageRange}
              inputVariant='area'
              min={filterLimits?.areaMin ?? 0}
              max={filterLimits?.areaMax ?? 10000}
              placeholders={getPlaceholdersForInputsWithRangeSlider(
                filterLimits?.areaMin,
                filterLimits?.areaMax,
                formatArea,
              )}
            />
          </Collapse>

          <Collapse {...getCollapseProps('Карта')}>
            <PolygonFilterData
              type={MarketTypes.SECONDARY}
              onClearPolygon={handleClearPolygon}
              selectedPolygonCount={selectedPolygonSecondary?.length ?? 0}
              polygons={(filterLimits?.polygons as IPolygonItemRequest[]) || []}
            />
          </Collapse>
        </div>
      )}
    </BaseScreen>
  );
};

export default BuySecondaryFilters;
